import { Box } from '@ff-it/ui';
import { timestamp } from 'components/columns';
import { Period } from 'components';
import { EnterpriseLink } from 'components/EnterpriseLink';
import { BookingStateBadge, PaymentStateBadge, InvoiceSource } from 'modules/invoicing/common';
import { fmtMonth } from 'utilities';
import type { ListIncome } from './types';
import { CompanyTitle } from 'modules/core/company/components';
import { IsLinkedStateBadge } from '../common/IsLinkedStateBadge';
import { DocumentApplicationStateBadge } from '../common/DocumentApplicationStateBadge';
import { LinkApplicationStateBadge } from '../common/LinkApplicationStateBadge';
import { InvoiceStateTag } from '../common/InvoiceStateTag';
import { InvoiceTypeIcon } from '../InvoiceTypeIcon';
import { ControlledTableColumns, flexColumn } from 'components/ControlledTable';

import { datesColumns, numberColumnWidth, totalsColumn } from '../common/columns';

export const columns: ControlledTableColumns<ListIncome> = [
  {
    content: 'Number',
    key: 'number',
    required: true,
    sortable: false,
    width: numberColumnWidth,
    cellProps: { className: 'text-nowrap' },
    // absolute link since we use it in nested lists
    render: ({ id, number, type }: ListIncome) => (
      <Box display="flex" alignItems="center" gap="1">
        <InvoiceTypeIcon direction="income" type={type} />
        <EnterpriseLink title={number} to={`/invoices/income/${id}`}>
          {number}
        </EnterpriseLink>
      </Box>
    ),
  },
  {
    content: 'State',
    width: 40,
    minWidth: 40,
    cellProps: { textAlign: 'center' },
    key: 'state',
    sortable: false,
    render: ({ state }) => <InvoiceStateTag state={state} appearance="naked" iconOnly />,
  },
  flexColumn({
    cellProps: { className: 'py-1' },
    content: 'Payer',
    key: 'payer.title',
    sortable: true,
    render: (income: ListIncome) => (
      <Box display="flex" alignItems="center" gap="1">
        <InvoiceSource {...income} />
        <Box style={{ minWidth: 0 }}>
          <CompanyTitle {...income.payer} />
        </Box>
      </Box>
    ),
  }),
  {
    cellProps: {
      className: 'py-0',
    },
    content: 'Service per.',
    key: 'date_from',
    render: ({ date_from, date_to }) => (date_from ? <Period date_from={date_from} date_to={date_to} /> : null),
    width: 120,
  },
  {
    cellProps: {
      className: 'py-0',
    },
    content: 'Finance per.',
    key: 'finance_period',
    render: ({ finance_period }) =>
      finance_period ? <Period date_from={finance_period.start} date_to={finance_period.end} fmt={fmtMonth} /> : null,
    width: 120,
  },
  timestamp('Created', 'created_at', { sortable: true, width: 140 }),
  datesColumns,
  totalsColumn,
  {
    width: 140,
    key: 'sums',
    configLabel: 'Statuses',
    render: (invoice: ListIncome) => (
      <Box display="flex" gap="1">
        <PaymentStateBadge state={invoice.sums.paym_state} />
        <BookingStateBadge state={invoice.state} booking_state={invoice.booking_state} />
        <IsLinkedStateBadge linked_at={invoice.linked_at} link_state={invoice.sums.link_state} />
        <LinkApplicationStateBadge direction={invoice.direction} link_state={invoice.sums.link_state} />
        <DocumentApplicationStateBadge invoice={invoice} />
      </Box>
    ),
  },
];
